import { GetUserConversations } from "../services/backend";
import { isSupabaseBackend } from "../services/config";

function findExistingConversation(conversations, matchedUserId) {
  if (!Array.isArray(conversations) || !matchedUserId) return null;
  const target = String(matchedUserId);
  return conversations.find((c) => {
    const otherId = c?.userId ?? c?.otherUserId ?? c?.matchUserId;
    return otherId != null && String(otherId) === target;
  });
}

/**
 * Ensures a conversation exists after a mutual match.
 * Supabase: match + conversation created by add_like RPC — just look up existing.
 * Azure legacy: SendBird channel + AddMatch (deprecated path when backend=azure).
 */
export async function ensureMatchConversation(currentUserId, matchedUser) {
  const matchedUserId = matchedUser?.id ?? matchedUser?.user?.id;
  if (!currentUserId || !matchedUserId) return null;

  try {
    const conversations = await GetUserConversations(currentUserId);
    const existing = findExistingConversation(conversations, matchedUserId);
    if (existing?.conversationId) {
      return existing.conversationId;
    }
  } catch (error) {
    console.warn("ensureMatchConversation: could not list conversations", error);
  }

  if (isSupabaseBackend()) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("payinapo:conversations-changed"));
    }
    return null;
  }

  // Legacy Azure + SendBird path (only when REACT_APP_BACKEND=azure)
  const { CreateChannel, GetChannelUrl } = await import("./SendBirdHttp");
  const { AddMatch } = await import("../services/backend");

  let channelUrl = null;
  try {
    channelUrl = await GetChannelUrl(currentUserId, matchedUserId);
    if (!channelUrl) {
      channelUrl = await CreateChannel(currentUserId, matchedUserId);
    }
  } catch (error) {
    console.error("ensureMatchConversation: SendBird channel failed", error);
    return null;
  }

  if (!channelUrl) return null;

  const matchRecords = [
    { matchForId: matchedUserId, userId: currentUserId, sendbirdUri: channelUrl },
    { matchForId: currentUserId, userId: matchedUserId, sendbirdUri: channelUrl },
  ];

  await Promise.all(
    matchRecords.map((record) =>
      AddMatch(record).catch((err) => {
        console.warn("ensureMatchConversation: AddMatch skipped or failed", err?.response?.data ?? err);
      })
    )
  );

  try {
    const conversations = await GetUserConversations(currentUserId);
    const created = findExistingConversation(conversations, matchedUserId);
    if (created?.conversationId) {
      return created.conversationId;
    }
  } catch (error) {
    console.warn("ensureMatchConversation: could not re-fetch conversations", error);
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("payinapo:conversations-changed"));
  }

  return channelUrl;
}
